import type { BlobLike } from "@pdfking/compatibility"
import { FileCache } from "@pdfking/compatibility"
import { BlobArray, BlobToUint8 } from "@pdfking/compatibility"
import type { AsyncSequence, Bytes } from "@pdfking/tfunc"
import {
  CopyIterable,
  GuidToString,
  IterableToArray,
  MapAsync,
  ReduceAsync,
  SafeIterable,
  UInt8Hash,
  UnixTime,
} from "@pdfking/tfunc"

import { AutoThrow } from "../errors/AutoThrow"
import type { PdfEngine } from "../types/engine/PdfEngine"
import type { GeneratedFile } from "../types/files/cached/GeneratedFile"
import type { ContentType } from "../types/files/ContentType"
import { VirtualFileType } from "../types/files/VirtualFileType"
import { TrackedTaskName } from "../types/tasks/tracking/TrackedTaskName"
import type { TrackingCallback } from "../types/tasks/tracking/TrackingCallback"

export const GenerateFile = async (
  { config: { tracking_chunk_size, file_cache } }: PdfEngine,
  stream: AsyncSequence<BlobLike>,
  content_type: ContentType,
  emit: TrackingCallback
): Promise<GeneratedFile> => {
  const { error, stream: copy } = SafeIterable(stream)

  const [chunks, body_size, body_hash] = CopyIterable(copy, 3)

  const guid = UInt8Hash(MapAsync(BlobToUint8, body_hash)).then(GuidToString)
  const size = await ReduceAsync(
    (acc: Bytes, blob: BlobLike) => (acc + blob.size) as Bytes,
    body_size,
    0 as Bytes
  )

  const emitter = (done: 0 | 1 = 0) => {
    if (size > tracking_chunk_size) {
      emit({ name: TrackedTaskName.GENERATE_FILE, done, total: 1 })
    }
  }

  emitter()

  await AutoThrow(error)

  const modified = UnixTime()

  // Store in cache using the hash as part of the key
  await FileCache(file_cache).set(
    await guid,
    await IterableToArray(chunks).then(BlobArray)
  )

  emitter(1)

  return {
    enum: VirtualFileType.GENERATED_FILE,
    type: content_type,
    guid: await guid,
    size,
    modified,
    file_meta: {},
  }
}
